import React, { useEffect, useState } from "react";
import "../styles/replace-guardian-email-modal.css";

export const LAST_GUARDIAN_REQUIRES_NEW_EMAIL =
    "This action cannot be completed without adding a new parent/guardian email.";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type ReplaceGuardianEmailModalProps = {
    open: boolean;
    currentEmail: string;
    remainingEmail?: string;
    athleteEmail: string;
    onClose: () => void;
    onConfirm: (newEmail: string | null) => void;
    loading?: boolean;
    error?: string;
};

function isValidEmail(value: string): boolean {
    return EMAIL_PATTERN.test(value);
}

export default function ReplaceGuardianEmailModal({
    open,
    currentEmail,
    remainingEmail,
    athleteEmail,
    onClose,
    onConfirm,
    loading = false,
    error,
}: ReplaceGuardianEmailModalProps) {
    const [newEmail, setNewEmail] = useState("");
    const [fieldError, setFieldError] = useState("");

    const remaining = (remainingEmail ?? "").trim();
    const lastGuardian = !remaining;

    useEffect(() => {
        if (open) {
            setNewEmail("");
            setFieldError("");
        }
    }, [open, currentEmail]);

    if (!open) return null;

    const trimmedNew = newEmail.trim();
    const hasNewEmail = trimmedNew.length > 0;

    function validateNewEmail(value: string): string {
        if (!isValidEmail(value)) {
            return "Please enter a valid parent/guardian email.";
        }
        if (value.toLowerCase() === currentEmail.trim().toLowerCase()) {
            return "New parent/guardian email must be different from the current email.";
        }
        if (value.toLowerCase() === athleteEmail.trim().toLowerCase()) {
            return "Parent/guardian email must be different from the athlete email.";
        }
        if (remaining && value.toLowerCase() === remaining.toLowerCase()) {
            return "Parent/guardian emails must be different.";
        }
        return "";
    }

    const newEmailError = hasNewEmail ? validateNewEmail(trimmedNew) : "";
    const confirmDisabled = loading
        || (lastGuardian && (!hasNewEmail || Boolean(newEmailError)))
        || (!lastGuardian && hasNewEmail && Boolean(newEmailError));

    const primaryLabel = lastGuardian || hasNewEmail
        ? (loading ? "Saving..." : "Replace and send login email")
        : (loading ? "Removing..." : "Remove");

    function handleConfirm() {
        if (lastGuardian && !hasNewEmail) {
            setFieldError(LAST_GUARDIAN_REQUIRES_NEW_EMAIL);
            return;
        }
        if (hasNewEmail) {
            const validation = validateNewEmail(trimmedNew);
            if (validation) {
                setFieldError(validation);
                return;
            }
            onConfirm(trimmedNew);
            return;
        }
        onConfirm(null);
    }

    return (
        <div className="replace-guardian-modal-overlay" onClick={onClose}>
            <div
                className="replace-guardian-modal-content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="replace-guardian-modal-title"
                data-testid="replace-guardian-email-modal"
            >
                <h2 id="replace-guardian-modal-title" className="replace-guardian-modal-title">
                    {lastGuardian ? "Replace parent/guardian email" : "Remove parent/guardian email"}
                </h2>
                {lastGuardian ? (
                    <p
                        className="replace-guardian-modal-alert"
                        role="alert"
                        data-testid="replace-guardian-email-alert"
                    >
                        {LAST_GUARDIAN_REQUIRES_NEW_EMAIL}
                    </p>
                ) : null}
                <p className="replace-guardian-modal-message">
                    {lastGuardian ? (
                        <>
                            Removing <strong>{currentEmail}</strong> will remove that parent/guardian&apos;s
                            access. This athlete account must stay linked to a parent/guardian. Their
                            progress will not be deleted.
                        </>
                    ) : (
                        <>
                            This athlete will still have <strong>{remaining}</strong> as a parent/guardian.
                            You can add a different email now, or just remove this one.
                        </>
                    )}
                </p>
                <label className="replace-guardian-modal-label" htmlFor="replace-guardian-email-input">
                    {lastGuardian ? "New parent/guardian email *" : "New parent/guardian email (optional)"}
                </label>
                <input
                    id="replace-guardian-email-input"
                    className="replace-guardian-modal-input"
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                        setNewEmail(e.target.value);
                        setFieldError("");
                    }}
                    disabled={loading}
                    data-testid="replace-guardian-email-input"
                    autoComplete="off"
                />
                {fieldError || newEmailError ? (
                    <p className="replace-guardian-modal-field-error" role="alert">
                        {fieldError || newEmailError}
                    </p>
                ) : null}
                {error ? (
                    <p className="replace-guardian-modal-error" role="alert">
                        {error}
                    </p>
                ) : null}
                <div className="replace-guardian-modal-actions">
                    <button
                        type="button"
                        className="replace-guardian-modal-btn replace-guardian-modal-cancel"
                        onClick={onClose}
                        disabled={loading}
                        data-testid="replace-guardian-email-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="replace-guardian-modal-btn replace-guardian-modal-confirm"
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                        data-testid="replace-guardian-email-confirm"
                    >
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
